import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useFilters } from '../context/FilterContext';
import * as api from '../services/api';

interface DashboardData {
  timestamp: string;
  storeInfo: any;
  cardMetrics: {
    total: any;
    dineIn: any;
    nonDineIn: any;
  };
  chartData: {
    diningGroups: any[];
    diningBehaviors: any[];
    dayOfWeek: any[];
    hourly: any[];
    dailyTrend: any[];
    monthlyTrend: any[];
    dayOfWeekFiltered?: any[];
    hourlyBehaviorFiltered?: any[];
    hourlyDiningGroupFiltered?: any[];
    dayOfWeekBehaviorFiltered?: any[];
    dayOfWeekDiningGroupFiltered?: any[];
    dayOfWeekMenuFiltered?: any[];
    hourlyMenuFiltered?: any[];
    behaviorMenuFiltered?: any[];
    diningGroupMenuFiltered?: any[];
  };
  detailedData?: {
    diningOptions: any;
    weekdayVsWeekend: any[];
    peakHours: any[];
    timePeriods: any[];
  };
  menuData?: {
    menuGroups: any[];
    topItems: any[];
    bottomItems: any[];
  };
  filters?: {
    dayOfWeek?: number[];
    hour?: number[];
    behavior?: string[];
    diningGroup?: string[];
    menuGroup?: string[];
  };
}

interface UseDashboardDataResult {
  data: DashboardData | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
  salesforceId: string | null;
}

const getSalesforceIdFromURL = (): string | null => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('storeId');
};


const getCurrentETDate = (): string => {
  const now = new Date();
  const etDate = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
  return etDate.toISOString().split('T')[0]; 
};

export const useDashboardData = (): UseDashboardDataResult => {
  
  const { 
    dateRange, 
    dayOfWeek, 
    time, 
    diningBehavior, 
    isLoadingDefaultDates 
  } = useFilters();
  
  const salesforceId = getSalesforceIdFromURL();
  
  
  
  const {
    data: kpiData,
    isLoading: kpiLoading,
    error: kpiError,
    refetch: kpiRefetch
  } = useQuery({
    queryKey: ['dashboard-kpi', getCurrentETDate(), salesforceId, dateRange, dayOfWeek, time, diningBehavior],
    queryFn: async () => {
      const filterParams = api.buildFilterParams({
        dateRange,
        dayOfWeek,
        time,
        diningBehavior
      });
      return api.getDashboardKpiOnly(salesforceId!, filterParams);
    },
    enabled: !isLoadingDefaultDates && !!salesforceId,
    staleTime: 12 * 60 * 60 * 1000,  
    gcTime: 24 * 60 * 60 * 1000,     
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  
  const {
    data: chartData,
    error: chartError,
    refetch: chartRefetch
  } = useQuery({
    queryKey: ['dashboard-charts', getCurrentETDate(), salesforceId, dateRange, dayOfWeek, time, diningBehavior],
    queryFn: async () => {
      const filterParams = api.buildFilterParams({
        dateRange,
        dayOfWeek,
        time,
        diningBehavior
      });
      return api.getDashboardChartsOnly(salesforceId!, filterParams);
    },
    enabled: !isLoadingDefaultDates && !!salesforceId && !!kpiData, 
    staleTime: 12 * 60 * 60 * 1000,  
    gcTime: 24 * 60 * 60 * 1000,     
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  
  const combinedData = useMemo(() => {
    if (!kpiData) return null;
    
    return {
      timestamp: kpiData.timestamp,
      period: kpiData.period,
      storeInfo: kpiData.storeInfo,
      cardMetrics: kpiData.cardMetrics,
      chartData: chartData?.chartData || {} 
    };
  }, [kpiData, chartData]);
  
  const error = kpiError ? 
    (kpiError instanceof Error ? kpiError.message : 'An error occurred while fetching KPI data') : 
    chartError ?
    (chartError instanceof Error ? chartError.message : 'An error occurred while fetching chart data') :
    null;
    
  const refetch = useCallback(() => {
    kpiRefetch();
    chartRefetch();
  }, [kpiRefetch, chartRefetch]);

  return { 
    data: combinedData,
    loading: kpiLoading,  
    error, 
    refetch, 
    salesforceId 
  };
};

export const getChartData = (
  data: DashboardData | null,
  chartType: 'hourly' | 'dayOfWeek' | 'diningBehavior' | 'diningGroup' | 'menu',
  filters?: {
    dayOfWeek?: number[];
    hour?: number[];
    behavior?: string[];
    diningGroup?: string[];
    menuGroup?: string[];
  }
) => {
  if (!data) return null;
  
  const hasHourFilter = filters?.hour && filters.hour.length > 0;
  const hasDayFilter = filters?.dayOfWeek && filters.dayOfWeek.length > 0;
  const hasBehaviorFilter = filters?.behavior && filters.behavior.length > 0;
  const hasDiningGroupFilter = filters?.diningGroup && filters.diningGroup.length > 0;
  
  
  switch (chartType) {
    case 'hourly':
      return data.chartData.hourly;
      
    case 'dayOfWeek':
      if (hasHourFilter && data.chartData.dayOfWeekFiltered) {
        return transformHourlyDayOfWeekToChartData(data.chartData.dayOfWeekFiltered);
      }
      if (hasDayFilter && data.chartData.dayOfWeekFiltered) {
        return transformHourlyDayOfWeekToChartData(data.chartData.dayOfWeekFiltered);
      }
      return data.chartData.dayOfWeek;
      
    case 'diningBehavior':
      if (hasHourFilter && data.chartData.hourlyBehaviorFiltered) {
        return transformHourlyBehaviorToChartData(data.chartData.hourlyBehaviorFiltered);
      } else if (hasDayFilter && data.chartData.dayOfWeekBehaviorFiltered) {
        return transformDayOfWeekBehaviorToChartData(data.chartData.dayOfWeekBehaviorFiltered);
      }
      return data.chartData.diningBehaviors;
      
    case 'diningGroup':
      if (hasHourFilter && data.chartData.hourlyDiningGroupFiltered) {
        return transformHourlyDiningGroupToChartData(data.chartData.hourlyDiningGroupFiltered);
      } else if (hasDayFilter && data.chartData.dayOfWeekDiningGroupFiltered) {
        return transformDayOfWeekDiningGroupToChartData(data.chartData.dayOfWeekDiningGroupFiltered);
      }
      return data.chartData.diningGroups;
      
    case 'menu':
      if (hasHourFilter && data.chartData.hourlyMenuFiltered) {
        return transformHourlyMenuToChartData(data.chartData.hourlyMenuFiltered);
      } else if (hasDayFilter && data.chartData.dayOfWeekMenuFiltered) {
        return transformDayOfWeekMenuToChartData(data.chartData.dayOfWeekMenuFiltered);
      } else if (hasBehaviorFilter && data.chartData.behaviorMenuFiltered) {
        return transformBehaviorMenuToChartData(data.chartData.behaviorMenuFiltered);
      } else if (hasDiningGroupFilter && data.chartData.diningGroupMenuFiltered) {
        return transformDiningGroupMenuToChartData(data.chartData.diningGroupMenuFiltered);
      }
      return data.menuData?.menuGroups || [];
      
    default:
      return null;
  }
};

const transformHourlyDayOfWeekToChartData = (hourlyDowData: any[]) => {
  const dayOfWeekMap = new Map<
    string,
    { day_of_week: number; day_name: string; net_sales: number; order_count: number }
  >();

  hourlyDowData.forEach(item => {
    const key = `${item.day_of_week}-${item.day_name}`;
    if (!dayOfWeekMap.has(key)) {
      dayOfWeekMap.set(key, {
        day_of_week: item.day_of_week,
        day_name: item.day_name,
        net_sales: 0,
        order_count: 0
      });
    }
    const existing = dayOfWeekMap.get(key)!;
    const netSales = parseFloat(item.net_sales.replace(/[^0-9.-]+/g, '')) || 0;
    const orderCount = parseInt(item.order_count || '0') || 0;
    existing.net_sales += netSales;
    existing.order_count += orderCount;
  });

  return Array.from(dayOfWeekMap.values()).map(item => ({
    day_of_week: item.day_of_week,
    day_name: item.day_name,
    net_sales: `$${item.net_sales.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    order_count: item.order_count.toString(),
    avg_daily_sales: `$${(
      item.net_sales /
      (item.order_count > 0 ? item.order_count : 1)
    ).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }));
};


const transformHourlyBehaviorToChartData = (hourlyBehaviorData: any[]) => {
  const behaviorMap = new Map<string, { behavior: string; net_sales: number; order_count: number }>();

  hourlyBehaviorData.forEach(item => {
    const key = item.behavior;
    if (!behaviorMap.has(key)) {
      behaviorMap.set(key, {
        behavior: item.behavior,
        net_sales: 0,
        order_count: 0
      });
    }
    const existing = behaviorMap.get(key)!;
    const netSales = parseFloat(item.net_sales.replace(/[^0-9.-]+/g, '')) || 0;
    const orderCount = parseInt(item.order_count || '0') || 0;
    existing.net_sales += netSales;
    existing.order_count += orderCount;
  });

  const totalSales = Array.from(behaviorMap.values()).reduce((sum, v) => sum + v.net_sales, 0);

  return Array.from(behaviorMap.values()).map(item => ({
    behavior: item.behavior,
    net_sales: `$${item.net_sales.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    order_count: item.order_count.toString(),
    percentage: totalSales > 0 ? `${((item.net_sales / totalSales) * 100).toFixed(2)}%` : '0%'
  }));
};


const transformDayOfWeekBehaviorToChartData = (dowBehaviorData: any[]) => {
  const behaviorMap = new Map<string, { behavior: string; net_sales: number; order_count: number }>();

  dowBehaviorData.forEach(item => {
    const key = item.behavior;
    if (!behaviorMap.has(key)) {
      behaviorMap.set(key, {
        behavior: item.behavior,
        net_sales: 0,
        order_count: 0
      });
    }
    const existing = behaviorMap.get(key)!;
    const netSales = parseFloat(item.net_sales.replace(/[^0-9.-]+/g, '')) || 0;
    const orderCount = parseInt(item.order_count || '0') || 0;
    existing.net_sales += netSales;
    existing.order_count += orderCount;
  });

  const totalSales = Array.from(behaviorMap.values()).reduce((sum, v) => sum + v.net_sales, 0);

  return Array.from(behaviorMap.values()).map(item => ({
    behavior: item.behavior,
    net_sales: `$${item.net_sales.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    order_count: item.order_count.toString(),
    percentage: totalSales > 0 ? `${((item.net_sales / totalSales) * 100).toFixed(2)}%` : '0%'
  }));
};


const transformHourlyDiningGroupToChartData = (hourlyDiningGroupData: any[]) => {
  const groupMap = new Map<string, { dining_group: string; net_sales: number; order_count: number }>();

  hourlyDiningGroupData.forEach(item => {
    const key = item.dining_group;
    if (!groupMap.has(key)) {
      groupMap.set(key, {
        dining_group: item.dining_group,
        net_sales: 0,
        order_count: 0
      });
    }
    const existing = groupMap.get(key)!;
    const netSales = parseFloat(item.net_sales.replace(/[^0-9.-]+/g, '')) || 0;
    const orderCount = parseInt(item.order_count || '0') || 0;
    existing.net_sales += netSales;
    existing.order_count += orderCount;
  });

  const totalSales = Array.from(groupMap.values()).reduce((sum, v) => sum + v.net_sales, 0);

  return Array.from(groupMap.values()).map(item => ({
    dining_group: item.dining_group,
    net_sales: `$${item.net_sales.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    order_count: item.order_count.toString(),
    percentage: totalSales > 0 ? `${((item.net_sales / totalSales) * 100).toFixed(2)}%` : '0%'
  }));
};


const transformDayOfWeekDiningGroupToChartData = (dowDiningGroupData: any[]) => {
  const groupMap = new Map<string, { dining_group: string; net_sales: number; order_count: number }>();

  dowDiningGroupData.forEach(item => {
    const key = item.dining_group;
    if (!groupMap.has(key)) {
      groupMap.set(key, {
        dining_group: item.dining_group,
        net_sales: 0,
        order_count: 0
      });
    }
    const existing = groupMap.get(key)!;
    const netSales = parseFloat(item.net_sales.replace(/[^0-9.-]+/g, '')) || 0;
    const orderCount = parseInt(item.order_count || '0') || 0;
    existing.net_sales += netSales;
    existing.order_count += orderCount;
  });

  const totalSales = Array.from(groupMap.values()).reduce((sum, v) => sum + v.net_sales, 0);

  return Array.from(groupMap.values()).map(item => ({
    dining_group: item.dining_group,
    net_sales: `$${item.net_sales.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    order_count: item.order_count.toString(),
    percentage: totalSales > 0 ? `${((item.net_sales / totalSales) * 100).toFixed(2)}%` : '0%'
  }));
};


const transformHourlyMenuToChartData = (hourlyMenuData: any[]) => {
  const menuMap = new Map<string, { menu_group: string; net_sales: number; order_count: number; quantity: number }>();

  hourlyMenuData.forEach(item => {
    const key = item.menu_group;
    if (!menuMap.has(key)) {
      menuMap.set(key, {
        menu_group: item.menu_group,
        net_sales: 0,
        order_count: 0,
        quantity: 0
      });
    }
    const existing = menuMap.get(key)!;
    const netSales = parseFloat(item.net_sales.replace(/[^0-9.-]+/g, '')) || 0;
    const orderCount = parseInt(item.order_count || '0') || 0;
    const quantity = parseInt(item.quantity || '0') || 0;
    existing.net_sales += netSales;
    existing.order_count += orderCount;
    existing.quantity += quantity;
  });

  const totalSales = Array.from(menuMap.values()).reduce((sum, v) => sum + v.net_sales, 0);

  return Array.from(menuMap.values()).map(item => ({
    menu_group: item.menu_group,
    net_sales: Number(item.net_sales.toFixed(2)),
    order_count: item.order_count,
    quantity: item.quantity,
    percentage: totalSales > 0 ? Number(((item.net_sales / totalSales) * 100).toFixed(2)) : 0
  }));
};


const transformDayOfWeekMenuToChartData = (dowMenuData: any[]) => {
  const menuMap = new Map<string, { menu_group: string; net_sales: number; order_count: number; quantity: number }>();

  dowMenuData.forEach(item => {
    const key = item.menu_group;
    if (!menuMap.has(key)) {
      menuMap.set(key, {
        menu_group: item.menu_group,
        net_sales: 0,
        order_count: 0,
        quantity: 0
      });
    }
    const existing = menuMap.get(key)!;
    const netSales = parseFloat(item.net_sales.replace(/[^0-9.-]+/g, '')) || 0;
    const orderCount = parseInt(item.order_count || '0') || 0;
    const quantity = parseInt(item.quantity || '0') || 0;
    existing.net_sales += netSales;
    existing.order_count += orderCount;
    existing.quantity += quantity;
  });

  const totalSales = Array.from(menuMap.values()).reduce((sum, v) => sum + v.net_sales, 0);

  return Array.from(menuMap.values()).map(item => ({
    menu_group: item.menu_group,
    net_sales: Number(item.net_sales.toFixed(2)),
    order_count: item.order_count,
    quantity: item.quantity,
    percentage: totalSales > 0 ? Number(((item.net_sales / totalSales) * 100).toFixed(2)) : 0
  }));
};


const transformBehaviorMenuToChartData = (behaviorMenuData: any[]) => {
  const menuMap = new Map<string, { menu_group: string; net_sales: number; order_count: number; quantity: number }>();

  behaviorMenuData.forEach(item => {
    const key = item.menu_group;
    if (!menuMap.has(key)) {
      menuMap.set(key, {
        menu_group: item.menu_group,
        net_sales: 0,
        order_count: 0,
        quantity: 0
      });
    }
    const existing = menuMap.get(key)!;
    const netSales = parseFloat(item.net_sales.replace(/[^0-9.-]+/g, '')) || 0;
    const orderCount = parseInt(item.order_count || '0') || 0;
    const quantity = parseInt(item.quantity || '0') || 0;
    existing.net_sales += netSales;
    existing.order_count += orderCount;
    existing.quantity += quantity;
  });

  const totalSales = Array.from(menuMap.values()).reduce((sum, v) => sum + v.net_sales, 0);

  return Array.from(menuMap.values()).map(item => ({
    menu_group: item.menu_group,
    net_sales: Number(item.net_sales.toFixed(2)),
    order_count: item.order_count,
    quantity: item.quantity,
    percentage: totalSales > 0 ? Number(((item.net_sales / totalSales) * 100).toFixed(2)) : 0
  }));
};


const transformDiningGroupMenuToChartData = (diningGroupMenuData: any[]) => {
  const menuMap = new Map<string, { menu_group: string; net_sales: number; order_count: number; quantity: number }>();

  diningGroupMenuData.forEach(item => {
    const key = item.menu_group;
    if (!menuMap.has(key)) {
      menuMap.set(key, {
        menu_group: item.menu_group,
        net_sales: 0,
        order_count: 0,
        quantity: 0
      });
    }
    const existing = menuMap.get(key)!;
    const netSales = parseFloat(item.net_sales.replace(/[^0-9.-]+/g, '')) || 0;
    const orderCount = parseInt(item.order_count || '0') || 0;
    const quantity = parseInt(item.quantity || '0') || 0;
    existing.net_sales += netSales;
    existing.order_count += orderCount;
    existing.quantity += quantity;
  });

  const totalSales = Array.from(menuMap.values()).reduce((sum, v) => sum + v.net_sales, 0);

  return Array.from(menuMap.values()).map(item => ({
    menu_group: item.menu_group,
    net_sales: Number(item.net_sales.toFixed(2)),
    order_count: item.order_count,
    quantity: item.quantity,
    percentage: totalSales > 0 ? Number(((item.net_sales / totalSales) * 100).toFixed(2)) : 0
  }));
};